/**
 * Shared helpers for the chat search panels (in-channel and all-chats), so the
 * two surfaces render timestamps and highlights identically.
 */

import type { ChatSearchMatchMode } from '@fias/platform-resources';

/** Compact absolute timestamp — search results span history, so relative ages read poorly. */
export function formatSearchTimestamp(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Splits `text` on each case-insensitive occurrence of any search term so the
 * matched words can be marked. Terms come from the user's own query, and the
 * pieces are rendered as React children (never as HTML), so there is no
 * injection path here.
 */
export function highlightSegments(
  text: string,
  terms: string[],
): Array<{ text: string; hit: boolean }> {
  const escaped = terms
    .filter((t) => t.length > 0)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (escaped.length === 0) return [{ text, hit: false }];
  const source = `(${escaped.join('|')})`;
  // `split` with a single capture group interleaves the captured matches into
  // the result, so odd indices are exactly the hits. Testing each piece with a
  // /g/ regex instead would be wrong — lastIndex carries between calls.
  return text
    .split(new RegExp(source, 'gi'))
    .map((piece, i) => ({ text: piece, hit: i % 2 === 1 }))
    .filter((seg) => seg.text.length > 0);
}

/**
 * Terms to mark in a result snippet for the given match mode. `contains`
 * matches the literal text, so the whole query is one term; the word modes
 * match each word independently.
 */
export function searchTermsForHighlight(
  trimmedQuery: string,
  hasTerms: boolean,
  matchMode: ChatSearchMatchMode = 'word',
): string[] {
  if (!hasTerms) return [];
  if (matchMode === 'contains') return [trimmedQuery];
  return trimmedQuery.split(/\s+/).filter(Boolean);
}

/** Empty-state hint per match mode, pointing at the broader mode. */
export const NO_MATCH_HINT: Record<ChatSearchMatchMode, string> = {
  word: 'Whole-word search. Try “Contains” to match part of a word.',
  contains: 'No message contains that text.',
};
